import { FC, useState, MouseEvent } from 'react';
import {
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableFooter,
    Box,
    Drawer,
    IconButton,
    Typography,
    Link,
    Tooltip,
    Popover,
    Divider,
    Chip,
    CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SyncIcon from '@mui/icons-material/Sync';
import moment from 'moment-timezone';

import Utils from '../../../../../components/Utils';
import ApiService from '../../../../../ApiService';
import theme from '../../../../../theme';
import CampaignPacingUtils from '../../../../../utils/CampaignPacingUtils';

import { Campaign, CampaignAdGroup, CampaignFlight, FlightAppliedFee, Platform } from '../../../../../types/Campaign';
import CampaignFlightAdGroupGrid from '../DataGrid/CampaignFlightAdGroupGrid';
import { useUserContext } from '../../../../../hooks/Contexts/UserContext';
import { useInfoMessage } from '../../../../../hooks/useInfoMessage';
import { AdminPanelSettingsOutlined as AdminPanelSettingsOutlinedIcon } from '@mui/icons-material';
import { getFlightStatus, calculateFlightSummary } from '../../../../../utils/FlightStatusUtils';

interface CampaignFlightSummaryTableProps {
    campaign: Campaign;
    onChange?: (campaign: Campaign) => void;
}

interface FeeEntry {
    label: string;
    value: number;
    type: 'percent' | 'fixed';
}

/** Per-flight state of the "sync" button, layered over the flight's dspSyncRequestedAt from the API. */
interface FlightSyncState {
    pending?: boolean;
    requestedAt?: string;
    nextAllowedAt?: string;
    error?: string;
}

/** Mirrors CampaignFlightSyncService on the API: a flight older than this cannot be synced. */
const FLIGHT_SYNC_MAX_AGE_DAYS = 70;
/** Mirrors the API's cooldown between two syncs of the same flight. */
const FLIGHT_SYNC_COOLDOWN_HOURS = 1;
const isFeeInitialized = (fee: FlightAppliedFee): boolean => {
    return (
        fee.agencyFee != null ||
        fee.agencyFixedCPM != null ||
        fee.agencyIncrementalCPM != null ||
        fee.cdPlatformFee != null ||
        fee.amazonPassthroughUpcharge != null ||
        fee.cdManagedService != null ||
        fee.otherFee != null
    );
};

const buildFeeEntries = (fee: FlightAppliedFee, campaign: Campaign): FeeEntry[] => {
    const entries: FeeEntry[] = [];

    if (fee.agencyFee) entries.push({ label: 'Agency Fee', value: fee.agencyFee, type: 'percent' });
    if (fee.cdManagedService)
        entries.push({ label: 'CD Managed Service', value: fee.cdManagedService, type: 'percent' });
    if (fee.cdPlatformFee && fee.includeCdPlatformFee)
        entries.push({ label: 'CD Platform Fee', value: fee.cdPlatformFee, type: 'fixed' });
    if (fee.otherFee && fee.includeOtherFee)
        entries.push({ label: fee.otherFeeLabel || 'Other Fee', value: fee.otherFee, type: 'fixed' });
    if (fee.agencyFixedCPM) entries.push({ label: 'Agency Fixed CPM', value: fee.agencyFixedCPM, type: 'fixed' });
    if (fee.agencyIncrementalCPM)
        entries.push({ label: 'Agency Incremental CPM', value: fee.agencyIncrementalCPM, type: 'fixed' });
    if (fee.cognitionCPM) entries.push({ label: 'Cognition CPM', value: fee.cognitionCPM, type: 'fixed' });
    if (campaign.platform === Platform.AMAZON) {
        if (fee.amazonPassthroughUpcharge && fee.includeAmazonPassthroughUpcharge)
            entries.push({ label: 'Amazon Passthrough', value: fee.amazonPassthroughUpcharge, type: 'percent' });
    } else if (campaign.platform === Platform.DV360) {
        if (fee.dv360PassthroughUpchargeYouTube && fee.includeDv360PassthroughUpchargeYouTube)
            entries.push({
                label: 'DV360 YouTube Passthrough',
                value: fee.dv360PassthroughUpchargeYouTube,
                type: 'percent',
            });
        if (fee.dv360PassthroughUpchargeOther && fee.includeDv360PassthroughUpchargeOther)
            entries.push({
                label: 'DV360 Other Passthrough',
                value: fee.dv360PassthroughUpchargeOther,
                type: 'percent',
            });
    }
    return entries;
};

const FeeSectionRows: FC<{ entries: FeeEntry[] }> = ({ entries }) => (
    <Box sx={{ mt: 0.5 }}>
        {entries.map((entry) => (
            <Box
                key={entry.label}
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 4,
                    py: 0.5,
                    px: 0.5,
                    borderRadius: 1,
                    '&:hover': { backgroundColor: 'action.hover' },
                }}
            >
                <Typography variant="body2" color="text.secondary">
                    {entry.label}
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                    {entry.value}
                </Typography>
            </Box>
        ))}
    </Box>
);

const FlightFeeSummary: FC<{ fee?: FlightAppliedFee; campaign: Campaign }> = ({ fee, campaign }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    if (!fee)
        return (
            <Typography variant="body2" color="text.disabled">
                —
            </Typography>
        );

    if (!isFeeInitialized(fee))
        return (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.75rem' }}>
                No fee data
            </Typography>
        );

    const entries = buildFeeEntries(fee, campaign);

    if (entries.length === 0)
        return (
            <Chip
                label="Pass-through"
                size="small"
                variant="outlined"
                sx={{
                    fontSize: '0.72rem',
                    color: 'text.secondary',
                    borderColor: 'divider',
                    height: 22,
                }}
            />
        );

    const percentEntries = entries.filter((e) => e.type === 'percent');
    const fixedEntries = entries.filter((e) => e.type === 'fixed');

    const handleOpen = (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
    };
    const handleClose = () => setAnchorEl(null);

    return (
        <>
            <Chip
                label={`${entries.length} fee${entries.length !== 1 ? 's' : ''}`}
                size="small"
                icon={<InfoOutlinedIcon />}
                onClick={handleOpen}
                sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
            />

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { p: 2, minWidth: 260 }, elevation: 4 }}
            >
                <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
                    Applied Fees
                </Typography>

                {percentEntries.length > 0 && (
                    <>
                        <Typography
                            variant="caption"
                            fontWeight={600}
                            color="text.secondary"
                            sx={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}
                        >
                            Percentage
                        </Typography>
                        <FeeSectionRows entries={percentEntries} />
                    </>
                )}

                {percentEntries.length > 0 && fixedEntries.length > 0 && <Divider sx={{ my: 1.5 }} />}

                {fixedEntries.length > 0 && (
                    <>
                        <Typography
                            variant="caption"
                            fontWeight={600}
                            color="text.secondary"
                            sx={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}
                        >
                            Fixed / CPM
                        </Typography>
                        <FeeSectionRows entries={fixedEntries} />
                    </>
                )}
            </Popover>
        </>
    );
};

const renderBudgetPacing = (value?: number) => {
    if (value == null)
        return (
            <Typography variant="body2" color="text.disabled">
                —
            </Typography>
        );
    const pct = Utils.round(value * 100, 0);
    const color = theme.palette[CampaignPacingUtils.getPacingColor(pct)].main;
    return <span style={{ fontWeight: 600, color }}>{pct}%</span>;
};

const CampaignFlightsSummaryTable: FC<CampaignFlightSummaryTableProps> = ({ campaign, onChange }) => {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedFlight, setSelectedFlight] = useState<CampaignFlight | null>(null);
    const [syncState, setSyncState] = useState<Record<number, FlightSyncState>>({});
    const { userContext } = useUserContext();
    const { showInfoMessage } = useInfoMessage();

    /**
     * What the sync button should show for a flight. The rules are the API's; this only
     * pre-computes them so the button is disabled with a reason instead of failing.
     */
    const getFlightSyncInfo = (flight: CampaignFlight): { disabled: boolean; label: string; pending: boolean } => {
        const state = syncState[flight.id] ?? {};
        const requestedAt = state.requestedAt ?? flight.dspSyncRequestedAt ?? null;
        const nextAllowed = state.nextAllowedAt
            ? moment(state.nextAllowedAt)
            : requestedAt
            ? moment(requestedAt).add(FLIGHT_SYNC_COOLDOWN_HOURS, 'hour')
            : null;
        const today = moment.tz('America/New_York').startOf('day');
        const start = moment.tz(flight.startDateTime, 'America/New_York').startOf('day');

        if (state.pending) {
            return { disabled: true, pending: true, label: 'Queuing performance sync…' };
        }
        if (start.isBefore(today.clone().subtract(FLIGHT_SYNC_MAX_AGE_DAYS, 'days'))) {
            return {
                disabled: true,
                pending: false,
                label: `Sync performance unavailable: flight started more than ${FLIGHT_SYNC_MAX_AGE_DAYS} days ago`,
            };
        }
        if (start.isAfter(today)) {
            return {
                disabled: true,
                pending: false,
                label: 'Sync performance unavailable: flight has not started yet',
            };
        }
        if (nextAllowed && moment().isBefore(nextAllowed)) {
            return {
                disabled: true,
                pending: false,
                label: `Performance sync queued ${moment(
                    requestedAt
                ).fromNow()} · the result lands within the hour · next sync ${nextAllowed.fromNow()}`,
            };
        }
        if (state.error) {
            return { disabled: false, pending: false, label: `${state.error} · click to retry` };
        }
        return {
            disabled: false,
            pending: false,
            label: requestedAt
                ? `Sync performance: re-aggregate every day of this flight (last sync ${moment(requestedAt).fromNow()})`
                : 'Sync performance: re-aggregate every day of this flight; the result lands within the hour',
        };
    };

    const syncFlight = async (flight: CampaignFlight) => {
        setSyncState((prev) => ({ ...prev, [flight.id]: { pending: true } }));
        try {
            const { data } = await ApiService.syncFlight(flight.id);
            setSyncState((prev) => ({
                ...prev,
                [flight.id]: { requestedAt: data.requestedAt, nextAllowedAt: data.nextAllowedAt },
            }));
            showInfoMessage(
                'success',
                `Performance sync queued for ${data.dates} day(s) of this flight. The result lands within the hour.`
            );
        } catch (e: unknown) {
            const response = (e as { response?: { data?: { message?: string; error?: string } } }).response;
            setSyncState((prev) => ({
                ...prev,
                [flight.id]: { error: response?.data?.message || response?.data?.error || 'Sync failed' },
            }));
        }
    };

    const AdminOnlyIcon = () => (
        <Tooltip title="Visible to Admins Only">
            <AdminPanelSettingsOutlinedIcon
                color="primary"
                sx={{ fontSize: 17, ml: 1, verticalAlign: 'text-bottom' }}
            />
        </Tooltip>
    );

    const aggregateSummary = calculateFlightSummary(campaign.flights);

    const getStatusBadge = (status: string, backgroundColor: string, color: string) => (
        <Box
            sx={{
                display: 'inline-block',
                backgroundColor,
                color,
                padding: '4px 16px',
                borderRadius: '16px',
                fontSize: '14px',
                fontWeight: 500,
            }}
        >
            {status}
        </Box>
    );

    const getLinkedAdGroups = (flight: CampaignFlight): CampaignAdGroup[] => {
        if (!campaign.adGroups) return [];

        const flightStart = moment.tz(flight.startDateTime, 'America/New_York').startOf('day');
        const flightEnd = moment.tz(flight.endDateTime, 'America/New_York').endOf('day');

        return campaign.adGroups.filter((adGroup) => {
            const adGroupStart = moment.tz(adGroup.startDateTime, 'America/New_York').startOf('day');
            const adGroupEnd = moment.tz(adGroup.endDateTime, 'America/New_York').endOf('day');

            return (
                (adGroupStart.isSameOrAfter(flightStart) && adGroupStart.isSameOrBefore(flightEnd)) ||
                (adGroupEnd.isSameOrAfter(flightStart) && adGroupEnd.isSameOrBefore(flightEnd)) ||
                (adGroupStart.isSameOrBefore(flightStart) && adGroupEnd.isSameOrAfter(flightEnd))
            );
        });
    };

    const handleOpenDrawer = (flight: CampaignFlight) => {
        setSelectedFlight(flight);
        setDrawerOpen(true);
    };

    const handleCloseDrawer = () => {
        setDrawerOpen(false);
        setSelectedFlight(null);
    };

    const getFlightNumber = (index: number) => campaign.flights.length - index;

    return (
        <>
            <TableContainer sx={{ width: '100%', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Table sx={{ width: '100%' }} aria-label="Campaign Flight Summary Table">
                    <TableHead>
                        <TableRow>
                            <TableCell>Flight No.</TableCell>
                            <TableCell align="left">Status</TableCell>
                            <TableCell align="right">Budget</TableCell>
                            {(userContext.isAdmin() || userContext.isOpsAdmin()) && (
                                <TableCell align="right">
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'flex-end',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        DSP Budget
                                        <AdminOnlyIcon />
                                    </Box>
                                </TableCell>
                            )}
                            <TableCell align="right">Spent</TableCell>
                            <TableCell align="right">Remaining</TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                Impression Goal
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                Impressions
                            </TableCell>
                            <TableCell align="right">Start Date</TableCell>
                            <TableCell align="right">End Date</TableCell>
                            {campaign.platform === Platform.DV360 && <TableCell align="left">Name</TableCell>}
                            <TableCell align="right">Pacing</TableCell>
                            {(userContext.isAdmin() || userContext.isOpsAdmin()) && (
                                <TableCell align="right">
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'flex-end',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        Fees
                                        <AdminOnlyIcon />
                                    </Box>
                                </TableCell>
                            )}
                            {(userContext.isAdmin() || userContext.isOpsAdmin()) && (
                                <TableCell align="right">
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'flex-end',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        Linked Ad Groups
                                        <AdminOnlyIcon />
                                    </Box>
                                </TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {campaign.flights.map((row, index) => {
                            const linkedAdGroups = getLinkedAdGroups(row);
                            const flightStatus = getFlightStatus(row.startDateTime, row.endDateTime);

                            return (
                                <TableRow key={row.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                    <TableCell component="th" scope="row">
                                        {getFlightNumber(index)}
                                    </TableCell>
                                    <TableCell align="left">
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                            }}
                                        >
                                            {getStatusBadge(
                                                flightStatus.status,
                                                flightStatus.backgroundColor,
                                                flightStatus.color
                                            )}
                                            {userContext.isSuperAdmin() && (
                                                <>
                                                    <Tooltip
                                                        title={
                                                            row.flightId ? `Connected to DSP` : 'Not connected to DSP'
                                                        }
                                                    >
                                                        {row.flightId ? (
                                                            <CheckCircleIcon
                                                                sx={{ fontSize: 16, color: 'success.main' }}
                                                            />
                                                        ) : (
                                                            <WarningAmberIcon
                                                                sx={{ fontSize: 16, color: 'warning.main' }}
                                                            />
                                                        )}
                                                    </Tooltip>
                                                    {row.flightId &&
                                                        (() => {
                                                            const sync = getFlightSyncInfo(row);
                                                            return (
                                                                <Tooltip title={sync.label}>
                                                                    <span>
                                                                        <IconButton
                                                                            size="small"
                                                                            aria-label="Sync performance"
                                                                            disabled={sync.disabled}
                                                                            onClick={(
                                                                                event: MouseEvent<HTMLButtonElement>
                                                                            ) => {
                                                                                event.stopPropagation();
                                                                                syncFlight(row);
                                                                            }}
                                                                            sx={{ p: 0.25 }}
                                                                        >
                                                                            {sync.pending ? (
                                                                                <CircularProgress size={14} />
                                                                            ) : (
                                                                                <SyncIcon
                                                                                    sx={{ fontSize: 16 }}
                                                                                    color={
                                                                                        syncState[row.id]?.error
                                                                                            ? 'error'
                                                                                            : 'inherit'
                                                                                    }
                                                                                />
                                                                            )}
                                                                        </IconButton>
                                                                    </span>
                                                                </Tooltip>
                                                            );
                                                        })()}
                                                </>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                        {row.budgetAmount ? Utils.formatCurrency(row.budgetAmount) : ' - '}
                                    </TableCell>
                                    {(userContext.isAdmin() || userContext.isOpsAdmin()) && (
                                        <TableCell align="right">
                                            {row.dspBudget ? Utils.formatCurrency(row.dspBudget) : ' - '}
                                        </TableCell>
                                    )}
                                    <TableCell align="right">
                                        {row.totalCost ? Utils.formatCurrency(row.totalCost) : ' - '}
                                    </TableCell>
                                    <TableCell align="right">
                                        {Utils.formatCurrency(row.budgetAmount - row.totalCost)}
                                    </TableCell>
                                    <TableCell align="right">
                                        {row.impressionGoal != null && row.impressionGoal > 0
                                            ? row.impressionGoal.toLocaleString()
                                            : ' - '}
                                    </TableCell>
                                    <TableCell align="right">
                                        {row.totalImpressions ? row.totalImpressions.toLocaleString() : ' - '}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'flex-end',
                                                gap: 0.5,
                                            }}
                                        >
                                            {Utils.formatESTDate(row.startDateTime)}
                                            <Tooltip
                                                title={`Flight starts at ${moment
                                                    .tz(row.startDateTime, 'America/New_York')
                                                    .format('MMMM D, YYYY h:mm A z')}`}
                                            >
                                                <InfoOutlinedIcon
                                                    sx={{ fontSize: 16, color: 'action.active', cursor: 'pointer' }}
                                                />
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'flex-end',
                                                gap: 0.5,
                                            }}
                                        >
                                            {Utils.formatESTDate(row.endDateTime)}
                                            <Tooltip
                                                title={`Flight ends at ${moment
                                                    .tz(row.endDateTime, 'America/New_York')
                                                    .format('MMMM D, YYYY h:mm A z')}`}
                                            >
                                                <InfoOutlinedIcon
                                                    sx={{ fontSize: 16, color: 'action.active', cursor: 'pointer' }}
                                                />
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                    {campaign.platform === Platform.DV360 && (
                                        <TableCell align="left">{row.name ?? '—'}</TableCell>
                                    )}
                                    <TableCell align="right">{renderBudgetPacing(row.budgetPacing)}</TableCell>
                                    {(userContext.isAdmin() || userContext.isOpsAdmin()) && (
                                        <TableCell align="right">
                                            <FlightFeeSummary fee={row.appliedFee} campaign={campaign} />
                                        </TableCell>
                                    )}
                                    {(userContext.isAdmin() || userContext.isOpsAdmin()) && (
                                        <TableCell align="left">
                                            {linkedAdGroups.length > 0 ? (
                                                <Link
                                                    component="button"
                                                    variant="body2"
                                                    onClick={() => handleOpenDrawer(row)}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        textDecoration: 'underline',
                                                        color: 'primary.main',
                                                        '&:hover': { color: 'primary.dark' },
                                                    }}
                                                >
                                                    {linkedAdGroups.length} Ad Group
                                                    {linkedAdGroups.length !== 1 ? 's' : ''}
                                                </Link>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    -
                                                </Typography>
                                            )}
                                        </TableCell>
                                    )}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                    <TableFooter
                        sx={{
                            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                            '& .MuiTableCell-root': {
                                color: (theme) => theme.palette.primary.main,
                                fontSize: (theme) => theme.typography.body2,
                                fontWeight: 500,
                            },
                        }}
                    >
                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                            <TableCell component="th" scope="row">
                                {campaign.flights.length}
                            </TableCell>
                            <TableCell align="right" colSpan={1}></TableCell>
                            <TableCell align="right">
                                {aggregateSummary.totalBudgetAmount
                                    ? Utils.formatCurrency(aggregateSummary.totalBudgetAmount)
                                    : ' - '}
                            </TableCell>
                            {(userContext.isAdmin() || userContext.isOpsAdmin()) && (
                                <TableCell align="right">
                                    {aggregateSummary.totalDspBudgetAmount
                                        ? Utils.formatCurrency(aggregateSummary.totalDspBudgetAmount)
                                        : ' - '}
                                </TableCell>
                            )}
                            <TableCell align="right">
                                {aggregateSummary.totalCost ? Utils.formatCurrency(aggregateSummary.totalCost) : ' - '}
                            </TableCell>
                            <TableCell align="right">
                                {aggregateSummary.totalRemaining
                                    ? Utils.formatCurrency(aggregateSummary.totalRemaining)
                                    : ' - '}
                            </TableCell>
                            <TableCell align="right">
                                {(() => {
                                    const totalGoal = campaign.flights.reduce(
                                        (sum, f) => sum + (f.impressionGoal ?? 0),
                                        0
                                    );
                                    return totalGoal > 0 ? totalGoal.toLocaleString() : ' - ';
                                })()}
                            </TableCell>
                            <TableCell align="right">
                                {(() => {
                                    const totalImps = campaign.flights.reduce(
                                        (sum, f) => sum + (f.totalImpressions ?? 0),
                                        0
                                    );
                                    return totalImps > 0 ? totalImps.toLocaleString() : ' - ';
                                })()}
                            </TableCell>
                            <TableCell align="right">
                                {aggregateSummary.earliestStartDate
                                    ? Utils.formatESTDate(aggregateSummary.earliestStartDate)
                                    : ' - '}
                            </TableCell>
                            <TableCell align="right">
                                {aggregateSummary.latestEndDate
                                    ? Utils.formatESTDate(aggregateSummary.latestEndDate)
                                    : ' - '}
                            </TableCell>
                            <TableCell align="right"></TableCell>
                            <TableCell align="right"></TableCell>
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>

            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={handleCloseDrawer}
                PaperProps={{
                    sx: { width: { xs: '100%', sm: '80%', md: '60%', lg: '50%' }, maxWidth: '800px' },
                }}
            >
                {selectedFlight && (
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box
                            sx={{
                                p: 2,
                                borderBottom: 1,
                                borderColor: 'divider',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="h6">
                                    Flight {getFlightNumber(campaign.flights.indexOf(selectedFlight))}
                                </Typography>
                                {(() => {
                                    const flightStatus = getFlightStatus(
                                        selectedFlight.startDateTime,
                                        selectedFlight.endDateTime
                                    );
                                    return getStatusBadge(
                                        flightStatus.status,
                                        flightStatus.backgroundColor,
                                        flightStatus.color
                                    );
                                })()}
                            </Box>
                            <IconButton onClick={handleCloseDrawer} edge="end">
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        <Box sx={{ flexGrow: 1, overflow: 'auto', p: 3 }}>
                            <CampaignFlightAdGroupGrid adGroups={getLinkedAdGroups(selectedFlight)} />
                        </Box>
                    </Box>
                )}
            </Drawer>
        </>
    );
};

export type { CampaignFlightSummaryTableProps };
export default CampaignFlightsSummaryTable;
